import type React from "react";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import {
  Visibility,
  Edit,
  CheckCircle,
  Schedule,
  Warning,
  Person,
  Assignment,
  School,
  Search,
  Add,
  Delete,
  Settings,
  Task,
  MenuBook,
  Download,
} from "@mui/icons-material";
import axios from "axios";
// import {
//   getOnboardingProgress,
//   completeOrientationTaskBulk,
//   sendOnboardingReminder,
//   sendRequiredDocuments,
//   updateUploadedDocumentStatus,
//   getUploadedDocuments,
//   setOrientationTasks,
//   sendOnboardingCourses,
// } from "../../../Services/OnboardingService2"
import CourseManagement from "./CourseManagement";
import styles from "./HROnboardingView.module.css";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
// import { getAllEmployees, type Employee } from "../../Services/LeaveService"
import {
  baseUrl,
  config,
  requestID,
  getUser,
  getBusinessData,
} from "../../common/utils";
import { getAllEmployees, Employee } from "../../api/LeaveService";
import {
  getOnboardingProgress,
  sendOnboardingReminder,
  completeOrientationTaskBulk,
  sendRequiredDocuments,
  setOrientationTasks,
  sendOnboardingCourses,
  getUploadedDocuments,
  updateUploadedDocumentStatus,
} from "../../api/OnboardingService2";
import { IonContent, IonPage } from "@ionic/react";
import { getApplicantByBusiness, getApplicantByTitle } from "../../api/jobs/jobs";

interface HROnboardingViewProps {
  employeeId?: string;
}

interface DocumentType {
  id: string;
  name: string;
  description?: string;
  required: boolean | string;
  category?: string;
  isDefault?: boolean;
}

interface OrientationTask {
  id: string;
  title: string;
  description: string;
  location?: string;
  responsiblePerson?: string;
  estimatedDuration?: string;
  required: boolean;
  o_id?: string;
  status?: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  type: "video" | "interactive" | "quiz" | "document";
  duration?: string;
  required: boolean;
  content?: string;
  status?: "active" | "inactive";
  course_id?: string;
}

interface OnboardingEmployee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  position: string;
  startDate: string;
  onboardingStatus: "not_started" | "in_progress" | "completed";
  currentStep: number;
  completionPercentage: number;
  documentsStatus: "pending" | "approved" | "rejected";
  orientationStatus: "pending" | "completed";
  coursesStatus: "pending" | "in_progress" | "completed";
  profileStatus: "incomplete" | "complete";
  certificateGenerated: boolean;
  lastActivity: string;
}

interface UploadedDocument {
  id: string;
  document_id: string;
  document_name: string;
  document_path: string;
  status: "uploaded" | "approved" | "rejected";
  created_at: string;
  user_id: string;
  onboarding_document_id: string;
  message: string;
  business_id: string;
  branch_id: string;
}

// Default document types that should always be available
const DEFAULT_DOCUMENT_TYPES: DocumentType[] = [
  { id: "passport", name: "Passport Copy", required: false, isDefault: true },
  { id: "id_card", name: "National ID Card", required: false, isDefault: true },
  { id: "resume", name: "Updated Resume", required: false, isDefault: true },
  {
    id: "certificates",
    name: "Educational Certificates",
    required: false,
    isDefault: true,
  },
  {
    id: "bank_details",
    name: "Bank Account Details",
    required: false,
    isDefault: true,
  },
  {
    id: "emergency_contact",
    name: "Emergency Contact Form",
    required: false,
    isDefault: true,
  },
  {
    id: "medical_certificate",
    name: "Medical Certificate",
    required: false,
    isDefault: true,
  },
  { id: "tax_forms", name: "Tax Forms", required: false, isDefault: true },
];

// Default orientation tasks
const DEFAULT_ORIENTATION_TASKS: OrientationTask[] = [
  {
    id: "office_tour",
    title: "Office Tour",
    description: "Complete guided tour of office facilities",
    location: "Main Office",
    responsiblePerson: "HR Representative",
    estimatedDuration: "30 minutes",
    required: true,
  },
  {
    id: "id_badge",
    title: "ID Badge Collection",
    description: "Collect employee ID badge and access cards",
    location: "Security Desk",
    responsiblePerson: "Security Officer",
    estimatedDuration: "15 minutes",
    required: true,
  },
  {
    id: "workstation_setup",
    title: "Workstation Setup",
    description: "Set up computer, phone, and workspace",
    location: "Assigned Desk",
    responsiblePerson: "IT Support",
    estimatedDuration: "45 minutes",
    required: true,
  },
  {
    id: "safety_briefing",
    title: "Safety Briefing",
    description: "Emergency procedures and safety protocols",
    location: "Conference Room A",
    responsiblePerson: "Safety Officer",
    estimatedDuration: "20 minutes",
    required: true,
  },
];






const HROnboardingView: React.FC = () => {
  const employeeId = getUser().userData?.user_id || "";

  const businessData = getBusinessData();

  const users = getAllEmployees();
  const history = useHistory();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobTitleFilter, setJobTitleFilter] = useState("all");
  const [tabValue, setTabValue] = useState(0);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [orientationDialogOpen, setOrientationDialogOpen] = useState(false);
  const [addOrientationDialogOpen, setAddOrientationDialogOpen] =
    useState(false);
  const [coursesDialogOpen, setCoursesDialogOpen] = useState(false);
  const [courseManagementOpen, setCourseManagementOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedOrientationTasks, setSelectedOrientationTasks] = useState<
    string[]
  >([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [customDocumentTypes, setCustomDocumentTypes] = useState<
    DocumentType[]
  >([]);
  const [documentManagementOpen, setDocumentManagementOpen] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState("");
  const [newDocumentDescription, setNewDocumentDescription] = useState("");
  const [newOrientationName, setNewOrientationName] = useState("");
  const [newOrientationDescription, setNewOrientationDescription] =
    useState("");
  const [newDocumentRequired, setNewDocumentRequired] = useState("0");
  const [addingDocument, setAddingDocument] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [getAllOrientationTasks, setGetAllOrientationTasks] = useState<
    OrientationTask[]
  >([]);
  const [uploadedDocumentsDialogOpen, setUploadedDocumentsDialogOpen] =
    useState(false);
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState<
    any[]
  >([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentStatusUpdateLoading, setDocumentStatusUpdateLoading] =
    useState<string | null>(null);
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);

  const busId = businessData?.business_id;
  const branchId = businessData?.branch_id;
  // const branchId = "bra_id_b96c58e8adabb47c038dc8dc5c00009m";
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Combine default and custom document types
  const allDocumentTypes = [...DEFAULT_DOCUMENT_TYPES, ...customDocumentTypes];

  const [jobTitles, setJobTitles] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    fetchOnboardingEmployees();
    fetchCustomDocumentTypes();
    handleGetAllOrientationTasks();
    fetchJobTitles();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, statusFilter]);



  const fetchOnboardingEmployees = async () => {
    const data ={
      business_id: businessData?.business_id,
      // selectedTitle : jobTitleFilter === "all" ? "" : jobTitleFilter
    }
    try {
      setLoading(true);
      const response = await getApplicantByBusiness(data);
      console.log("Fetched employees????:", response.data);

      setEmployees(response.data);
    } catch (err) {
      setError("Failed to load onboarding employees");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  //   const fetchOrientationTasks = async () => {
  //   const data ={
  //     business_id: businessData?.business_id,
      
  //   }
  //   try {
  //     setLoading(true);
  //     const response = await getApplicantByBusiness(data);
  //     // console.log("Fetched employees????:", response.data);

  //     setEmployees(response.data);
  //   } catch (err) {
  //     setError("Failed to load onboarding employees");
  //     console.error(err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };





  const fetchJobTitles = async () => {
    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}/jobs/getJobTitles`,
        payload,
        config
      );
      console.log("Job titles response:", res);
      setJobTitles(res.data.data);
    } catch (err: any) {
      showSnackbar("Failed to fetch job titles.", "error");
      console.error("Fetch job titles error:", err);
    }
  };

  const fetchCustomDocumentTypes = async () => {
    try {
      const user = getUser().userData;
      const response = await axios.post(
        `${baseUrl()}documents/getDocumentsType`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
        },
        config
      );

      console.log("Response from getDocumentsType:", response);

      if (response.data.status && Array.isArray(response.data.data)) {
        const customDocs = response.data.data
          .map((doc: any) => ({
            id: doc.document_type_id || doc.document_id,
            name: doc.name || doc.document_name,
            description: doc.description || "no description",
            required: doc.is_required || false,
            category: doc.category || "onboarding",
          }))
          .filter(
            (doc: DocumentType) =>
              !DEFAULT_DOCUMENT_TYPES.some(
                (defaultDoc) => defaultDoc.id === doc.id
              )
          );

        setCustomDocumentTypes(customDocs);
      } else {
        setCustomDocumentTypes([]);
      }
    } catch (err) {
      console.error("Error fetching document types:", err);
    }
  };

  const handleAddDocumentType = async () => {
    if (!newDocumentName.trim()) return;

    try {
      setAddingDocument(true);
      const user = getUser().userData;
      const response = await axios.post(
        `${baseUrl()}documents/addDocumentsTypes`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
          name: newDocumentName.trim(),
          required: newDocumentRequired,
        },
        config
      );

      console.log("Response from addDocumentsTypes:", response);
      if (!response.data.status) {
        console.error("Failed to add document type:", response.data.message);
        setError("Failed to add document type: " + response.data.message);
        return;
      }

      const data = response.data;
      if (data.status) {
        await fetchCustomDocumentTypes();
        setNewDocumentName("");
        setNewDocumentDescription("");
        setNewDocumentRequired("0");
        setMessage("Document type added successfully!");
        setTimeout(() => setMessage(null), 3000);
      } else {
        setError("Failed to add document type: " + data.message);
      }
    } catch (err) {
      setError("Error adding document type");
      console.error(err);
    } finally {
      setAddingDocument(false);
    }
  };



  const handleCreateOrientationTask = async () => {
    if (!newOrientationDescription.trim()) return;

    try {
      setAddingDocument(true);
      const user = getUser().userData;
      const response = await axios.post(
        `${baseUrl()}onboardings/createOrientation`,
        {
          requestID: requestID(),
          // business_id: user.bus_id || busId,
          // branch_id: user.bra_id || branchId,
          description: newOrientationDescription.trim(),
          // required: newDocumentRequired,
        },
        config
      );

      console.log("Response from createOrientation:", response);
      if (!response.data.status) {
        console.error("Failed to add orientation type:", response.data.message);
        setError("Failed to add orientation type: " + response.data.message);
        return;
      }

      // const data = response.data
      // if (data.status) {
      //   await fetchCustomDocumentTypes()
      //   setNewDocumentName("")
      //   setNewDocumentDescription("")
      //   setNewDocumentRequired("0")
      //   console.log("Document type added successfully")
      const data = response.data;
      if (data.status) {
        setNewOrientationName("");
        setNewOrientationDescription("");
        setNewDocumentRequired("0");
        console.log("Orientation task added successfully");
        setMessage("Orientation task added successfully");
        setAddOrientationDialogOpen(false);
      } else {
        setError("Failed to add document type: " + data.message);
      }
    } catch (err) {
      setError("Error adding document type");
      console.error(err);
    } finally {
      setAddingDocument(false);
    }
  };





  // const fetchOrientationTasks = async () => {

  //   try {
     
  //     const response = await axios.post(
  //       `${baseUrl()}onboardings/fetchOrientationTasks`,
  //       {
  //         requestID: requestID(),
 
  //       },
  //       config
  //     );

  //     console.log("Response from createOrientation:", response);
  //     if (!response.data.status) {
  //       console.error("Failed to add orientation type:", response.data.message);
  //       setError("Failed to add orientation type: " + response.data.message);
  //       return;
  //     }

  //     const data = response.data;
  //     if (data.status) {
  //       // setOrientationTasks()
      
  //       setMessage("Orientation task added successfully");
  //       setAddOrientationDialogOpen(false);
  //     } else {
  //       setError("Failed to add document type: " + data.message);
  //     }
  //   } catch (err) {
  //     setError("Error adding document type");
  //     console.error(err);
  //   } finally {
  //     setAddingDocument(false);
  //   }
  // };





  const handleDeleteDocumentType = async (documentId: string) => {
    try {
      if (DEFAULT_DOCUMENT_TYPES.some((doc) => doc.id === documentId)) {
        setError("Cannot delete default document types");
        return;
      }
      setCustomDocumentTypes((prev) =>
        prev.filter((doc) => doc.id !== documentId)
      );
    } catch (err) {
      setError("Error deleting document type");
      console.error(err);
    }
  };

  // Pagination logic
  const totalItems = filteredEmployees.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const filterEmployees = () => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(
        (emp) =>
          emp?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp?.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (emp) => emp.onboardingStatus === statusFilter
      );
    }

    setFilteredEmployees(filtered);
  };

  const handleViewEmployee = async (employee: Employee) => {
    try {
      const detailedProgress = await getOnboardingProgress(employee.applicant_id);
      setSelectedEmployee({ ...employee, ...detailedProgress });
      setDetailDialogOpen(true);
    } catch (err) {
      setError("Failed to load employee details");
      console.error(err);
    }
  };

  const handleNavigateToEmployee = (employeeId: string) => {
    history.push(`/onboarding/${employeeId}`);
  };

  const handleSendReminder = async (employeeId: string) => {
    try {
      await sendOnboardingReminder(
        [employeeId],
        "general",
        "Please complete your onboarding tasks."
      );
      setMessage("Reminder sent successfully!");
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError("Failed to send reminder");
      console.error(err);
    }
  };

  const handleBulkAction = async (action: string) => {
    try {
      switch (action) {
        case "complete_orientation":
          const allTaskIds = ["task1", "task2", "task3"];
          await completeOrientationTaskBulk(
            selectedEmployees,
            allTaskIds,
            "Bulk completion by HR"
          );
          break;
        case "send_reminders":
          await sendOnboardingReminder(
            selectedEmployees,
            "general",
            "Please complete your onboarding tasks."
          );
          break;
        case "send_documents":
          setDocumentDialogOpen(true);
          return;
        case "send_orientation":
          setOrientationDialogOpen(true);
          return;
        case "send_courses":
          setCoursesDialogOpen(true);
          return;
      }
      setBulkActionDialogOpen(false);
      setSelectedEmployees([]);
      fetchOnboardingEmployees();
    } catch (err) {
      setError("Failed to perform bulk action");
      console.error(err);
    }
  };

  const getStatusChip = (
    status: string,
    type: "overall" | "documents" | "orientation" | "courses" | "profile"
  ) => {
    const getColor = () => {
      switch (status) {
        case "completed":
        case "approved":
        case "complete":
          return "success";
        case "in_progress":
          return "primary";
        case "pending":
        case "incomplete":
          return "warning";
        case "rejected":
          return "error";
        default:
          return "default";
      }
    };

    const getIcon = () => {
      switch (status) {
        case "completed":
        case "approved":
        case "complete":
          return <CheckCircle />;
        case "in_progress":
          return <Schedule />;
        case "pending":
        case "incomplete":
          return <Warning />;
        default:
          return undefined;
      }
    };

    return (
      <Chip
        label={status?.replace("_", " ")}
        color={getColor()}
        size="small"
        icon={getIcon()}
      />
    );
  };

  const getOverallStats = () => {
    const total = employees?.length;
    const completed = employees?.filter(
      (emp) => emp.onboardingStatus === "completed"
    ).length;
    const inProgress = employees?.filter(
      (emp) => emp.onboardingStatus === "in_progress"
    ).length;
    const notStarted = employees?.filter(
      (emp) => emp.onboardingStatus === "not_started"
    ).length;

    return { total, completed, inProgress, notStarted };
  };

  const stats = getOverallStats();

  const handleSendDocuments = async () => {
    // console.log("Selected Employees:", selectedEmployees);
    // console.log("Selected Documents:", selectedDocuments);
    try {
     const res =  await sendRequiredDocuments(
        selectedEmployees,
        selectedDocuments,
        "Please upload the following required documents.",
        busId,
        branchId
      );


      console.log({res})

      setDocumentDialogOpen(false);
      setBulkActionDialogOpen(false);
      setSelectedEmployees([]);
      setSelectedDocuments([]);
      fetchOnboardingEmployees();
      setMessage("Document requirements sent successfully!");
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setError("Failed to send document requirements");
      console.error(err);
    }
  };



  const handleGetAllOrientationTasks = async () => {
    try {
      const response = await axios.post(
        `${baseUrl()}onboardings/getAllOrientationTasks`,
        {
          requestID: requestID(),
        },
        config
      );
      console.log("API Response:", response);
      setGetAllOrientationTasks(response.data.data || []);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching orientation tasks:", error);
      throw new Error("Failed to fetch orientation tasks");
    }
  };

  const handleActivateTasks = async (taskIds: string, status: string) => {
    try {
      const response = await axios.post(
        `${baseUrl()}onboardings/changeOrientationTasksStatus`,
        {
          requestID: requestID(),
          o_id: taskIds,
          currentStatus: status,
        },
        config
      );

      handleGetAllOrientationTasks();
      console.log("API Response:", response);
      if (response.data.status) {
        // setMessage("Orientation tasks activated successfully!")
        // setMessage("Orientation tasks status updated successfully!")
        setMessage(
          `Orientation tasks ${
            status === "0" ? "activated" : "deactivated"
          } successfully!`
        );
        setTimeout(() => setMessage(null), 4000);
        setAddOrientationDialogOpen(false);
        setNewOrientationName("");
        setNewOrientationDescription("");
      } else {
        setError(
          "Failed to activate orientation tasks: " + response.data.message
        );
      }
    } catch (error) {
      console.error("Error activating orientation tasks:", error);
      setError("Failed to activate orientation tasks");
    }
  };

  const handleEmployeePerformOrientation = async (taskIds: string) => {
    try {
      const response = await axios.post(
        `${baseUrl()}onboardings/getActivitiesByOId`,
        {
          requestID: requestID(),
          // user_id: employeeId,
          o_id: taskIds,
        },
        config
      );
      console.log("API Response:", response);
      if (response.data.status) {
        setMessage("Orientation tasks completed successfully!");
        setTimeout(() => setMessage(null), 4000);
        setDetailDialogOpen(false);
        fetchOnboardingEmployees();
      } else {
        setError(
          "Failed to complete orientation tasks: " + response.data.message
        );
      }
    } catch (error) {
      console.error("Error completing orientation tasks:", error);
      setError("Failed to complete orientation tasks");
    }
  };

  const handleSendOrientationTasks = async () => {
    // console.log("Selected Employees:", selectedEmployees);
    // console.log("Selected Orientation Tasks:", selectedOrientationTasks);
    try {
      await setOrientationTasks(
        selectedEmployees,
        selectedOrientationTasks,
        "Please complete the following orientation tasks.",
        busId,
        branchId
      );

      setOrientationDialogOpen(false);
      setBulkActionDialogOpen(false);
      setSelectedEmployees([]);
      setSelectedOrientationTasks([]);
      fetchOnboardingEmployees();

      setMessage("Orientation tasks sent successfully!");
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setError("Failed to send orientation tasks");
      console.error(err);
    }
  };

  const handleSendCourses = async () => {
    // console.log("Selected Employees:", selectedEmployees);
    // console.log("Selected Courses:", selectedCourses);
    try {
      await sendOnboardingCourses(
        selectedEmployees,
        selectedCourses,
        "Please complete the following onboarding courses.",
        busId,
        branchId
      );

      setCoursesDialogOpen(false);
      setBulkActionDialogOpen(false);
      setSelectedEmployees([]);
      setSelectedCourses([]);
      fetchOnboardingEmployees();

      setMessage("Courses sent successfully!");
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setError("Failed to send courses");
      console.error(err);
    }
  };

  const handleViewUploadedDocuments = async (employee: Employee) => {
    try {
      setLoadingDocuments(true);
      const user = getUser().userData;
      const documents = await getUploadedDocuments(
        employee.user_id,
        user.bus_id || busId,
        user.bra_id || branchId
      );
      setSelectedEmployeeDocuments(documents || []);
      setSelectedEmployee(employee);
      setUploadedDocumentsDialogOpen(true);
    } catch (err) {
      setError("Failed to load uploaded documents");
      console.error(err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleUpdateDocumentStatus = async (
    documentId: string,
    newStatus: "approved" | "rejected",
    Oid: string
  ) => {
    try {
      setDocumentStatusUpdateLoading(documentId);
      const user = getUser().userData;
      console.log("Updating document status:", {
        employeeId: selectedEmployee?.user_id || "",
        businessId: user.bus_id || busId,
        branchId: user.bra_id || branchId,
        documentId,
        newStatus,
        Oid,
      });
      const response = await updateUploadedDocumentStatus(
        selectedEmployee?.user_id || "",
        user.bus_id || busId,
        user.bra_id || branchId,
        documentId,
        newStatus,
        Oid
      );

      console.log("Update Document Status Response:", response);

      // Refresh the documents list
      const updatedDocuments = await getUploadedDocuments(
        selectedEmployee?.user_id || "",
        user.bus_id || busId,
        user.bra_id || branchId
      );
      setSelectedEmployeeDocuments(updatedDocuments || []);

      setMessage(`Document ${newStatus} successfully!`);
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(`Failed to ${newStatus} document`);
      console.error(err);
    } finally {
      setDocumentStatusUpdateLoading(null);
    }
  };

  const handleDownloadDocument = (
    documentPath: string,
    documentName: string
  ) => {
    try {
      // Parse the document path if it's a JSON string
      let path = documentPath;
      if (documentPath.startsWith("[") && documentPath.endsWith("]")) {
        const parsedPath = JSON.parse(documentPath);
        path = Array.isArray(parsedPath) ? parsedPath[0] : parsedPath;
      }

      // Create download link
      const fullPath = `${baseUrl()}${path}`;
      const link = document.createElement("a");
      link.href = fullPath;
      link.download = documentName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError("Failed to download document");
      console.error(err);
    }
  };

  const getDocumentStatusChip = (status: string) => {
    const getColor = () => {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "error";
        case "uploaded":
          return "warning";
        default:
          return "default";
      }
    };

    const getIcon = () => {
      switch (status) {
        case "approved":
          return <CheckCircle />;
        case "rejected":
          return <Warning />;
        case "uploaded":
          return <Schedule />;
        default:
          return undefined;
      }
    };

    return (
      <Chip label={status} color={getColor()} size="small" icon={getIcon()} />
    );
  };

  if (loading) {
    return (
      <Card className={styles.card}>
        <CardContent>
          <Typography>Loading onboarding data...</Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        className={styles.error}
        onClose={() => setError(null)}
      >
        {error}
      </Alert>
    );
  }

  if (message) {
    return (
      <Alert
        severity="success"
        className={styles.success}
        onClose={() => setMessage(null)}
      >
        {message}
      </Alert>
    );
  }

  return (
    <IonPage className="pushDown">
      <IonContent className="ion-padding">
        <div className={styles.container}>
          {/* Statistics Cards */}
          <Box className={styles.statsContainer}>
            <Card className={styles.statCard}>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Employees
                </Typography>
              </CardContent>
            </Card>
            <Card className={styles.statCard}>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {stats.completed}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
            <Card className={styles.statCard}>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {stats.inProgress}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  In Progress
                </Typography>
              </CardContent>
            </Card>
            <Card className={styles.statCard}>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  {stats.notStarted}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Not Started
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Main Content */}
          <Card className={styles.mainCard}>
            <CardHeader
              title="Employee Onboarding Management"
              subheader="Monitor and manage employee onboarding progress"
              action={
                <Box className={styles.headerActions}>
                  <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={() => setDocumentManagementOpen(true)}
                    className={styles.manageDocsButton}
                  >
                    Manage Documents
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setAddOrientationDialogOpen(true)}
                    className={styles.addOrientationButton}
                  >
                    Add Orientation Task
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<MenuBook />}
                    onClick={() => setCourseManagementOpen(true)}
                    className={styles.manageCourseButton}
                  >
                    Manage Courses
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setBulkActionDialogOpen(true)}
                    disabled={selectedEmployees.length === 0}
                  >
                    Bulk Actions ({selectedEmployees.length})
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {/* Filters and Search */}
              <Box className={styles.filtersContainer}>
                <TextField
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search />,
                  }}
                  className={styles.searchField}
                />

                <FormControl className={styles.filterSelect}>
                  <InputLabel>Job Title Filter</InputLabel>
                  <Select
                    value={jobTitleFilter}
                    onChange={(e) => setJobTitleFilter(e.target.value)}
                  >
                    {jobTitles.map((title) => {
                      return (
                        <MenuItem value={title.title}>
                          {title.title}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl className={styles.filterSelect}>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="not_started">Not Started</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Employees Table */}
              <TableContainer className={styles.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees(
                                filteredEmployees.map((emp) => emp.applicant_id)
                              );
                            } else {
                              setSelectedEmployees([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Overall Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Documents</TableCell>
                      <TableCell>Orientation</TableCell>
                      <TableCell>Courses</TableCell>
                      <TableCell>Profile</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedEmployees?.map((employee) => (
                      <TableRow
                        key={employee?.user_id}
                        className={styles.tableRow}
                      >
                        <TableCell padding="checkbox">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(
                              employee?.user_id
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEmployees([
                                  ...selectedEmployees,
                                  employee.user_id,
                                ]);
                              } else {
                                setSelectedEmployees(
                                  selectedEmployees.filter(
                                    (id) => id !== employee.user_id
                                  )
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box className={styles.employeeCell}>
                            <Avatar className={styles.avatar}>
                              {employee?.firstName?.[0]}
                              {employee?.lastName?.[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {employee.firstName} {employee.lastName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {employee.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {employee.department}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {employee.position}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(employee.startDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(employee.onboardingStatus, "overall")}
                        </TableCell>
                        <TableCell>
                          <Box className={styles.progressCell}>
                            <LinearProgress
                              variant="determinate"
                              value={
                                typeof employee.completionPercentage ===
                                "number"
                                  ? employee.completionPercentage
                                  : 0
                              }
                              className={styles.progressBar}
                            />
                            <Typography variant="caption">
                              {employee.completionPercentage ?? 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(employee.documentsStatus, "documents")}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(
                            employee.orientationStatus,
                            "orientation"
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(employee.coursesStatus, "courses")}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(employee.profileStatus, "profile")}
                        </TableCell>
                        <TableCell>
                          <Box className={styles.actionsCell}>
                            <IconButton
                              onClick={() => handleViewEmployee(employee)}
                              size="small"
                              title="View Details"
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                handleViewUploadedDocuments(employee)
                              }
                              size="small"
                              title="View Documents"
                            >
                              <Assignment />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                handleNavigateToEmployee(employee.user_id)
                              }
                              size="small"
                              title="Edit"
                            >
                              <Edit />
                            </IconButton>
                            <Button
                              size="small"
                              onClick={() =>
                                handleSendReminder(employee.user_id)
                              }
                              disabled={
                                employee.onboardingStatus === "completed"
                              }
                            >
                              Remind
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {getAllOrientationTasks.length > 0 ? (
                <List className={styles.orientationTasksList}>
                  {getAllOrientationTasks.map((task) => (
                    <ListItem
                      key={task.id}
                      className={styles.orientationTaskItem}
                    >
                      <ListItemText
                        primary={task.title}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {task.description}
                            </Typography>
                            {task.location && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Location: {task.location}
                              </Typography>
                            )}
                            {task.responsiblePerson && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Responsible: {task.responsiblePerson}
                              </Typography>
                            )}
                            {task.estimatedDuration && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Duration: {task.estimatedDuration}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="start"
                          onClick={() =>
                            handleActivateTasks(task.o_id, task.status)
                          }
                          size="small"
                          title={
                            task.status === "1"
                              ? "Deactivate Task"
                              : "Activate Task"
                          }
                        >
                          {task.status === "1" ? (
                            <Task style={{ color: "green" }} />
                          ) : (
                            <Task style={{ color: "gray" }} />
                          )}
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() =>
                            handleEmployeePerformOrientation(task.o_id)
                          }
                          size="small"
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteDocumentType(task.id)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>

                        <IconButton
                          edge="end"
                          onClick={() => handleEditDocumentType(task.id)}
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box className={styles.emptyState}>
                  <Typography variant="body2" color="textSecondary">
                    No orientation tasks available. Add new tasks to get
                    started.
                  </Typography>
                </Box>
              )}
            </CardContent>

            <Box className={styles.pagination}>
              <Button
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Typography variant="body2" className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Box>
          </Card>

          {/* Course Management Dialog */}
          <Dialog
            open={courseManagementOpen}
            onClose={() => setCourseManagementOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>Course Management</DialogTitle>
            <DialogContent>
              <CourseManagement onCoursesChange={setAvailableCourses} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCourseManagementOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Document Management Dialog */}
          <Dialog
            open={documentManagementOpen}
            onClose={() => setDocumentManagementOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box className={styles.dialogTitleContainer}>
                <Typography variant="h6">Manage Document Types</Typography>
                <Typography variant="body2" color="textSecondary">
                  Add and manage document types for your organization
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              {/* Add New Document Form */}
              <Card className={styles.addDocumentCard}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    className={styles.addDocumentTitle}
                  >
                    Add New Document Type
                  </Typography>
                  <Box className={styles.addDocumentForm}>
                    <TextField
                      label="Document Name"
                      value={newDocumentName}
                      onChange={(e) => setNewDocumentName(e.target.value)}
                      fullWidth
                      required
                      className={styles.formField}
                    />
                    <TextField
                      label="Description"
                      value={newDocumentDescription}
                      onChange={(e) =>
                        setNewDocumentDescription(e.target.value)
                      }
                      fullWidth
                      multiline
                      rows={2}
                      className={styles.formField}
                    />
                    <FormControl className={styles.formField}>
                      <InputLabel>Required</InputLabel>
                      <Select
                        value={newDocumentRequired}
                        onChange={(e) => setNewDocumentRequired(e.target.value)}
                      >
                        <MenuItem value="0">Optional</MenuItem>
                        <MenuItem value="1">Required</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleAddDocumentType}
                      disabled={!newDocumentName.trim() || addingDocument}
                      startIcon={<Add />}
                      className={styles.addButton}
                    >
                      {addingDocument ? "Adding..." : "Add Document"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Default Document Types */}
              <Typography
                variant="subtitle1"
                className={styles.existingDocsTitle}
              >
                Default Document Types
              </Typography>
              <List className={styles.documentList}>
                {DEFAULT_DOCUMENT_TYPES.map((doc) => (
                  <ListItem key={doc.id} className={styles.documentListItem}>
                    <ListItemText
                      primary={doc.name}
                      secondary={
                        <Box>
                          {doc.description && (
                            <Typography variant="body2" color="textSecondary">
                              {doc.description}
                            </Typography>
                          )}
                          <Chip
                            label="Default"
                            size="small"
                            color="primary"
                            className={styles.defaultChip}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {/* Custom Document Types */}
              <Divider className={styles.divider} />
              <Typography
                variant="subtitle1"
                className={styles.existingDocsTitle}
              >
                Custom Document Types ({customDocumentTypes.length})
              </Typography>
              <List className={styles.documentList}>
                {customDocumentTypes.map((doc) => (
                  <ListItem key={doc.id} className={styles.documentListItem}>
                    <ListItemText
                      primary={doc.name}
                      secondary={
                        <Box>
                          {doc.description && (
                            <Typography variant="body2" color="textSecondary">
                              {doc.description}
                            </Typography>
                          )}
                          <Chip
                            label={
                              doc.required == "1" ? "Required" : "Optional"
                            }
                            size="small"
                            color={doc.required == "1" ? "error" : "default"}
                            className={styles.requiredChip}
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteDocumentType(doc.id)}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              {customDocumentTypes.length === 0 && (
                <Box className={styles.emptyState}>
                  <Typography variant="body2" color="textSecondary">
                    No custom document types found. Add your first document type
                    above.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDocumentManagementOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Orientation Task Dialog */}
          <Dialog
            open={addOrientationDialogOpen}
            onClose={() => setAddOrientationDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Add New Orientation Task</DialogTitle>
            <DialogContent>
              <TextField
                label="Task Name"
                value={newOrientationName}
                onChange={(e) => setNewOrientationName(e.target.value)}
                fullWidth
                required
                className={styles.formField}
              />
              <TextField
                label="Description"
                value={newOrientationDescription}
                onChange={(e) => setNewOrientationDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                className={styles.formField}
              />
              <FormControl className={styles.formField}>
                <InputLabel>Required</InputLabel>
                <Select
                  value={newDocumentRequired}
                  onChange={(e) => setNewDocumentRequired(e.target.value)}
                >
                  <MenuItem value="0">Optional</MenuItem>
                  <MenuItem value="1">Required</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddOrientationDialogOpen(false)}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateOrientationTask}
                disabled={!newOrientationDescription.trim() || addingDocument}
                startIcon={<Add />}
                className={styles.addButton}
              >
                {addingDocument ? "Adding..." : "Add Orientation Task"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Employee Detail Dialog */}
          <Dialog
            open={detailDialogOpen}
            onClose={() => setDetailDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Employee Onboarding Details
              {selectedEmployee && (
                <Typography variant="subtitle1" color="textSecondary">
                  {selectedEmployee.firstName} {selectedEmployee.lastName}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              {selectedEmployee && (
                <Box className={styles.detailContent}>
                  <Typography variant="h6">Progress Overview</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={selectedEmployee.completionPercentage}
                    className={styles.detailProgressBar}
                  />
                  <Typography variant="body2" className={styles.progressText}>
                    {selectedEmployee.completionPercentage}% Complete
                  </Typography>

                  <Box className={styles.detailSections}>
                    <Box className={styles.detailSection}>
                      <Assignment />
                      <Typography variant="subtitle2">Documents</Typography>
                      {getStatusChip(
                        selectedEmployee.documentsStatus,
                        "documents"
                      )}
                    </Box>
                    <Box className={styles.detailSection}>
                      <Person />
                      <Typography variant="subtitle2">Orientation</Typography>
                      {getStatusChip(
                        selectedEmployee.orientationStatus,
                        "orientation"
                      )}
                    </Box>
                    <Box className={styles.detailSection}>
                      <School />
                      <Typography variant="subtitle2">Courses</Typography>
                      {getStatusChip(selectedEmployee.coursesStatus, "courses")}
                    </Box>
                    <Box className={styles.detailSection}>
                      <EmojiEventsIcon />
                      <Typography variant="subtitle2">Profile</Typography>
                      {getStatusChip(selectedEmployee.profileStatus, "profile")}
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
              {selectedEmployee && (
                <Button
                  variant="contained"
                  onClick={() =>
                    handleNavigateToEmployee(selectedEmployee.user_id)
                  }
                  startIcon={<Edit />}
                >
                  Manage Employee
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Bulk Actions Dialog */}
          <Dialog
            open={bulkActionDialogOpen}
            onClose={() => setBulkActionDialogOpen(false)}
          >
            <DialogTitle>Bulk Actions</DialogTitle>
            <DialogContent>
              <Typography variant="body1" className={styles.bulkActionText}>
                Select an action to perform on {selectedEmployees.length}{" "}
                selected employees:
              </Typography>
              <Box className={styles.bulkActions}>
                {/* <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleBulkAction("complete_orientation")}
                  className={styles.bulkActionButton}
                >
                  Complete Orientation Tasks
                </Button> */}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleBulkAction("send_reminders")}
                  className={styles.bulkActionButton}
                >
                  Send Reminders
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleBulkAction("send_documents")}
                  className={styles.bulkActionButton}
                  startIcon={<Assignment />}
                >
                  Send Required Documents
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleBulkAction("send_orientation")}
                  className={styles.bulkActionButton}
                  startIcon={<Task />}
                >
                  Send Orientation Tasks
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleBulkAction("send_courses")}
                  className={styles.bulkActionButton}
                  startIcon={<MenuBook />}
                >
                  Send Onboarding Courses
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBulkActionDialogOpen(false)}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* Document Selection Dialog */}
          <Dialog
            open={documentDialogOpen}
            onClose={() => setDocumentDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Send Required Documents</DialogTitle>
            <DialogContent>
              <Typography variant="body1" className={styles.bulkActionText}>
                Select documents to request from {selectedEmployees.length}{" "}
                selected employees:
              </Typography>
              <Box className={styles.documentSelect} sx={{ marginTop: "16px" }}>
                {/* Default Documents */}
                {DEFAULT_DOCUMENT_TYPES.map((doc) => (
                  <Box key={doc.id} className={styles.documentItem}>
                    <input
                      type="checkbox"
                      id={doc.id}
                      checked={selectedDocuments.includes(doc.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDocuments([...selectedDocuments, doc.id]);
                        } else {
                          setSelectedDocuments(
                            selectedDocuments.filter((id) => id !== doc.id)
                          );
                        }
                      }}
                    />
                    <label htmlFor={doc.id}>{doc.name}</label>
                  </Box>
                ))}

                {/* Custom Documents */}
                {customDocumentTypes.length > 0 && (
                  <>
                    <Divider className={styles.documentDivider} />
                    <Typography
                      variant="subtitle2"
                      className={styles.customDocumentsTitle}
                    >
                      Custom Documents
                    </Typography>
                    {customDocumentTypes.map((doc) => (
                      <Box key={doc.id} className={styles.documentItem}>
                        <input
                          type="checkbox"
                          id={doc.id}
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDocuments([
                                ...selectedDocuments,
                                doc.id,
                              ]);
                            } else {
                              setSelectedDocuments(
                                selectedDocuments.filter((id) => id !== doc.id)
                              );
                            }
                          }}
                        />
                        <label htmlFor={doc.id}>
                          <Box>
                            <Typography variant="body2">{doc.name}</Typography>
                            {doc.required == "1" && (
                              <Chip
                                label="Required"
                                size="small"
                                color="error"
                                className={styles.requiredChip}
                              />
                            )}
                          </Box>
                        </label>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDocumentDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendDocuments}
                disabled={selectedDocuments.length === 0}
              >
                Send Documents ({selectedDocuments.length})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Orientation Tasks Selection Dialog */}
          <Dialog
            open={orientationDialogOpen}
            onClose={() => setOrientationDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Send Orientation Tasks</DialogTitle>
            <DialogContent>
              <Typography variant="body1" className={styles.bulkActionText}>
                Select orientation tasks to assign to {selectedEmployees.length}{" "}
                selected employees:
              </Typography>
              <Box className={styles.documentSelect} sx={{ marginTop: "16px" }}>
                {getAllOrientationTasks.map((task,i) => (
                  <Box key={task.task_id} className={styles.documentItem}>
                    <input
                      type="checkbox"
                      id={task.task_id}
                      checked={selectedOrientationTasks.includes(task.task_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOrientationTasks([
                            ...selectedOrientationTasks,
                            task.task_id,
                          ]);
                        } else {
                          setSelectedOrientationTasks(
                            selectedOrientationTasks.filter(
                              (id) => id !== task.task_id
                            )
                          );
                        }
                      }}
                    />
                    <label htmlFor={task.task_id}>
                      <Box>
                        <Typography variant="body2">{task.title}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {task.description}
                        </Typography>
                        {task.required && (
                          <Chip
                            label="Required"
                            size="small"
                            color="error"
                            className={styles.requiredChip}
                          />
                        )}
                      </Box>
                    </label>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOrientationDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendOrientationTasks}
                disabled={selectedOrientationTasks.length === 0}
              >
                Send Tasks ({selectedOrientationTasks.length})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Courses Selection Dialog */}
          <Dialog
            open={coursesDialogOpen}
            onClose={() => setCoursesDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Send Onboarding Courses</DialogTitle>
            <DialogContent>
              <Typography variant="body1" className={styles.bulkActionText}>
                Select courses to assign to {selectedEmployees.length} selected
                employees:
              </Typography>
              <CourseManagement
                selectionMode={true}
                selectedCourses={selectedCourses}
                onCourseSelection={setSelectedCourses}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCoursesDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendCourses}
                disabled={selectedCourses.length === 0}
              >
                Send Courses ({selectedCourses.length})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Uploaded Documents Dialog */}
          <Dialog
            open={uploadedDocumentsDialogOpen}
            onClose={() => setUploadedDocumentsDialogOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box className={styles.dialogTitleContainer}>
                <Typography variant="h6">Uploaded Documents</Typography>
                {selectedEmployee && (
                  <Typography variant="body2" color="textSecondary">
                    {selectedEmployee.firstName} {selectedEmployee.lastName} -{" "}
                    {selectedEmployee.email}
                  </Typography>
                )}
              </Box>
            </DialogTitle>
            <DialogContent>
              {loadingDocuments ? (
                <Box className={styles.loadingContainer}>
                  <LinearProgress />
                  <Typography variant="body2" className={styles.loadingText}>
                    Loading documents...
                  </Typography>
                </Box>
              ) : selectedEmployeeDocuments.length > 0 ? (
                <TableContainer className={styles.documentsTableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Document Name</TableCell>
                        <TableCell>Upload Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedEmployeeDocuments.map(
                        (document: UploadedDocument) => (
                          <TableRow key={document.id}>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={styles.documentName}
                              >
                                {document.document_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(
                                  document.created_at
                                ).toLocaleDateString()}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {new Date(
                                  document.created_at
                                ).toLocaleTimeString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {getDocumentStatusChip(document.status)}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={styles.documentMessage}
                              >
                                {document.message || "No message"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box className={styles.documentActionsCell}>
                                <IconButton
                                  onClick={() =>
                                    handleDownloadDocument(
                                      document.document_path,
                                      document.document_name
                                    )
                                  }
                                  size="small"
                                  title="Download Document"
                                >
                                  <Download />
                                </IconButton>
                                {document.status === "uploaded" && (
                                  <>
                                    <Button
                                      variant="contained"
                                      color="success"
                                      size="small"
                                      onClick={() =>
                                        handleUpdateDocumentStatus(
                                          document.document_id,
                                          "approved",
                                          document.onboarding_document_id
                                        )
                                      }
                                      disabled={
                                        documentStatusUpdateLoading ===
                                        document.document_id
                                      }
                                      className={styles.approveButton}
                                      sx={{ margin: "2px" }}
                                    >
                                      {documentStatusUpdateLoading ===
                                      document.document_id
                                        ? "Approving..."
                                        : "Approve"}
                                    </Button>
                                    <Button
                                      variant="contained"
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        handleUpdateDocumentStatus(
                                          document.document_id,
                                          "rejected",
                                          document.onboarding_document_id
                                        )
                                      }
                                      disabled={
                                        documentStatusUpdateLoading ===
                                        document.document_id
                                      }
                                      className={styles.rejectButton}
                                      sx={{ margin: "2px" }}
                                    >
                                      {documentStatusUpdateLoading ===
                                      document.document_id
                                        ? "Rejecting..."
                                        : "Reject"}
                                    </Button>
                                  </>
                                )}
                                {document.status === "approved" && (
                                  <Chip
                                    label="Approved"
                                    color="success"
                                    size="small"
                                  />
                                )}
                                {document.status === "rejected" && (
                                  <Chip
                                    label="Rejected"
                                    color="error"
                                    size="small"
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box className={styles.emptyDocumentsState}>
                  <Assignment className={styles.emptyIcon} />
                  <Typography variant="h6" color="textSecondary">
                    No Documents Uploaded
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    This employee hasn't uploaded any documents yet.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUploadedDocumentsDialogOpen(false)}>
                Close
              </Button>
              {selectedEmployeeDocuments.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => handleViewUploadedDocuments(selectedEmployee!)}
                  startIcon={<Visibility />}
                >
                  Refresh
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default HROnboardingView;
